import { Rect } from './bitmap';
import { ReceiptFontWeight } from './fonts';

/**
 * A tiny layout engine for receipt-shaped documents.
 *
 * The painter works in two phases. While you call text()/row()/rule()... it measures each
 * block with a scratch context, records a drawing closure and advances a cursor. Nothing is
 * drawn yet, so the total height is known before any canvas is allocated. paint() then
 * replays the closures onto a real (supersampled) context, optionally offset so a long
 * receipt can be split into several images at the cut points recorded with cut().
 *
 * All units are paper dots at 1x; supersampling is handled by the caller's ctx.scale().
 */

export type TextAlign = 'left' | 'center' | 'right';

export interface TextStyle {
  size: number;
  weight: ReceiptFontWeight;
  align?: TextAlign;
  /** Extra spacing between characters in dots. */
  letterSpacing?: number;
  uppercase?: boolean;
  /** Line height multiplier, default 1.25. */
  leading?: number;
}

export interface TextOptions {
  /** Left indent inside the content area. */
  indent?: number;
  /** Max width, defaults to the content width minus indent. */
  maxWidth?: number;
  /** Max number of wrapped lines before truncating with an ellipsis. */
  maxLines?: number;
}

export interface ColumnCell {
  text: string;
  /** Column width in dots. */
  width: number;
  align?: TextAlign;
}

type Op = (ctx: CanvasRenderingContext2D) => void;

const ELLIPSIS = '…';

export class Painter {
  private ops: Op[] = [];
  private cuts: number[] = [0];
  private ditherRects: Rect[] = [];
  private y = 0;

  constructor(
    readonly width: number,
    readonly margin: number,
    private readonly fontFamily: string,
    private readonly measureCtx: CanvasRenderingContext2D,
  ) {}

  get contentWidth() {
    return this.width - this.margin * 2;
  }

  get height() {
    return this.y;
  }

  /** Moves the cursor, used to place several blocks side by side (e.g. quantity | title | total). */
  moveTo(y: number) {
    this.y = y;
  }

  /** Regions that should be dithered instead of thresholded (logos). */
  get imageRects(): Rect[] {
    return this.ditherRects;
  }

  font(style: TextStyle) {
    return `${style.weight} ${style.size}px ${this.fontFamily}`;
  }

  private prepare(text: string, style: TextStyle) {
    return style.uppercase ? text.toUpperCase() : text;
  }

  /** Width of a string in dots, including letter spacing. */
  measure(text: string, style: TextStyle) {
    this.measureCtx.font = this.font(style);
    const spacing = style.letterSpacing ?? 0;
    const width = this.measureCtx.measureText(text).width;
    return width + (text.length > 0 ? spacing * (text.length - 1) : 0);
  }

  /** Shortens text with an ellipsis so it fits within maxWidth. */
  truncate(text: string, style: TextStyle, maxWidth: number) {
    if (this.measure(text, style) <= maxWidth) {
      return text;
    }

    let end = text.length;
    while (end > 0 && this.measure(text.slice(0, end).trimEnd() + ELLIPSIS, style) > maxWidth) {
      end--;
    }

    return text.slice(0, end).trimEnd() + ELLIPSIS;
  }

  /** Greedy word wrap; words wider than the line are broken by character. */
  wrap(text: string, style: TextStyle, maxWidth: number): string[] {
    const lines: string[] = [];

    text.split('\n').forEach((paragraph) => {
      const words = paragraph.split(/\s+/).filter(Boolean);

      if (!words.length) {
        lines.push('');
        return;
      }

      let current = '';

      const pushWord = (word: string) => {
        const candidate = current ? `${current} ${word}` : word;

        if (this.measure(candidate, style) <= maxWidth) {
          current = candidate;
          return;
        }

        if (current) {
          lines.push(current);
          current = '';
        }

        if (this.measure(word, style) <= maxWidth) {
          current = word;
          return;
        }

        // Break an overlong word by character
        let chunk = '';
        for (const char of word) {
          if (this.measure(chunk + char, style) > maxWidth && chunk) {
            lines.push(chunk);
            chunk = '';
          }
          chunk += char;
        }
        current = chunk;
      };

      words.forEach(pushWord);

      if (current) {
        lines.push(current);
      }
    });

    return lines;
  }

  private lineHeight(style: TextStyle) {
    return Math.round(style.size * (style.leading ?? 1.25));
  }

  /** Draws one line of text at a baseline-independent vertical centre. */
  private drawLine(
    ctx: CanvasRenderingContext2D,
    text: string,
    style: TextStyle,
    left: number,
    right: number,
    centerY: number,
  ) {
    ctx.font = this.font(style);
    ctx.textBaseline = 'middle';
    ctx.fillStyle = ctx.fillStyle || '#000';

    const align = style.align ?? 'left';
    const spacing = style.letterSpacing ?? 0;
    const width = this.measure(text, style);

    let x = left;
    if (align === 'center') {
      x = left + (right - left - width) / 2;
    } else if (align === 'right') {
      x = right - width;
    }

    if (!spacing) {
      ctx.textAlign = 'left';
      ctx.fillText(text, x, centerY);
      return;
    }

    ctx.textAlign = 'left';
    for (const char of text) {
      ctx.fillText(char, x, centerY);
      x += ctx.measureText(char).width + spacing;
    }
  }

  /** Wrapped paragraph. Returns the height consumed. */
  text(raw: string, style: TextStyle, options: TextOptions = {}) {
    const text = this.prepare(raw, style);
    const indent = options.indent ?? 0;
    const maxWidth = options.maxWidth ?? this.contentWidth - indent;
    let lines = this.wrap(text, style, maxWidth);

    if (options.maxLines && lines.length > options.maxLines) {
      lines = lines.slice(0, options.maxLines);
      lines[lines.length - 1] = this.truncate(lines[lines.length - 1] + ELLIPSIS, style, maxWidth);
    }

    const lh = this.lineHeight(style);
    const top = this.y;
    const left = this.margin + indent;
    const right = left + maxWidth;

    this.ops.push((ctx) => {
      lines.forEach((line, i) => {
        this.drawLine(ctx, line, style, left, right, top + lh * i + lh / 2);
      });
    });

    this.y += lh * lines.length;
    return lh * lines.length;
  }

  /**
   * Label on the left, value on the right, on the same first line.
   * The left side wraps in the space the right side leaves free.
   */
  row(
    leftText: string,
    rightText: string,
    leftStyle: TextStyle,
    rightStyle: TextStyle = leftStyle,
    options: { indent?: number; gap?: number } = {},
  ) {
    const indent = options.indent ?? 0;
    const gap = options.gap ?? 12;
    const left = this.prepare(leftText, leftStyle);
    const right = this.prepare(rightText, rightStyle);

    const rightWidth = this.measure(right, rightStyle);
    const leftMax = Math.max(40, this.contentWidth - indent - rightWidth - gap);
    const leftLines = this.wrap(left, leftStyle, leftMax);

    const leftLh = this.lineHeight(leftStyle);
    const rightLh = this.lineHeight(rightStyle);
    const firstLine = Math.max(leftLh, rightLh);
    const top = this.y;
    const x0 = this.margin + indent;
    const x1 = this.margin + this.contentWidth;

    this.ops.push((ctx) => {
      leftLines.forEach((line, i) => {
        const centerY =
          i === 0 ? top + firstLine / 2 : top + firstLine + leftLh * (i - 1) + leftLh / 2;
        this.drawLine(ctx, line, { ...leftStyle, align: 'left' }, x0, x0 + leftMax, centerY);
      });
      this.drawLine(ctx, right, { ...rightStyle, align: 'right' }, x0, x1, top + firstLine / 2);
    });

    const height = firstLine + leftLh * Math.max(0, leftLines.length - 1);
    this.y += height;
    return height;
  }

  /** One table row with fixed column widths; cells are truncated, never wrapped. */
  columns(cells: ColumnCell[], style: TextStyle, options: { indent?: number } = {}) {
    const lh = this.lineHeight(style);
    const top = this.y;
    let x = this.margin + (options.indent ?? 0);

    const placed = cells.map((cell) => {
      const text = this.truncate(this.prepare(cell.text, style), style, cell.width);
      const item = { text, left: x, right: x + cell.width, align: cell.align ?? 'left' };
      x += cell.width;
      return item;
    });

    this.ops.push((ctx) => {
      placed.forEach((cell) => {
        this.drawLine(
          ctx,
          cell.text,
          { ...style, align: cell.align },
          cell.left,
          cell.right,
          top + lh / 2,
        );
      });
    });

    this.y += lh;
    return lh;
  }

  /** Inverted label: white text on a black rounded block, centred. */
  badge(raw: string, style: TextStyle, options: { paddingX?: number; paddingY?: number } = {}) {
    const text = this.prepare(raw, style);
    const px = options.paddingX ?? 14;
    const py = options.paddingY ?? 6;
    const maxTextWidth = this.contentWidth - px * 2;
    const label = this.truncate(text, style, maxTextWidth);
    const textWidth = this.measure(label, style);
    const width = textWidth + px * 2;
    const height = this.lineHeight(style) + py * 2;
    const top = this.y;
    const left = this.margin + (this.contentWidth - width) / 2;
    const radius = Math.min(8, height / 2);

    this.ops.push((ctx) => {
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.moveTo(left + radius, top);
      ctx.arcTo(left + width, top, left + width, top + height, radius);
      ctx.arcTo(left + width, top + height, left, top + height, radius);
      ctx.arcTo(left, top + height, left, top, radius);
      ctx.arcTo(left, top, left + width, top, radius);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#fff';
      this.drawLine(
        ctx,
        label,
        { ...style, align: 'center' },
        left,
        left + width,
        top + height / 2,
      );
      ctx.fillStyle = '#000';
    });

    this.y += height;
    return height;
  }

  /** Horizontal rule across the content width. */
  rule(thickness = 2, options: { dashed?: boolean; inset?: number } = {}) {
    const top = this.y;
    const inset = options.inset ?? 0;
    const x0 = this.margin + inset;
    const x1 = this.margin + this.contentWidth - inset;

    this.ops.push((ctx) => {
      ctx.fillStyle = '#000';
      if (options.dashed) {
        const dash = 6;
        const gapWidth = 5;
        for (let x = x0; x < x1; x += dash + gapWidth) {
          ctx.fillRect(x, top, Math.min(dash, x1 - x), thickness);
        }
      } else {
        ctx.fillRect(x0, top, x1 - x0, thickness);
      }
    });

    this.y += thickness;
    return thickness;
  }

  /** Vertical whitespace. */
  space(height: number) {
    this.y += height;
    return height;
  }

  /** Centred image scaled to the given width (defaults to full content width). */
  image(
    img: CanvasImageSource,
    naturalWidth: number,
    naturalHeight: number,
    maxWidth?: number,
    maxHeight = 200,
  ) {
    const targetMax = maxWidth ?? this.contentWidth;
    const scale = Math.min(1, targetMax / naturalWidth, maxHeight / naturalHeight);
    const width = Math.round(naturalWidth * scale);
    const height = Math.round(naturalHeight * scale);
    const left = Math.round(this.margin + (this.contentWidth - width) / 2);
    const top = this.y;

    this.ditherRects.push({ x: left, y: top, width, height });

    this.ops.push((ctx) => {
      ctx.drawImage(img, left, top, width, height);
    });

    this.y += height;
    return height;
  }

  /** Marks the current position as a safe place to split the print between images. */
  cut() {
    if (this.cuts[this.cuts.length - 1] !== this.y) {
      this.cuts.push(this.y);
    }
  }

  /**
   * Splits the document into [start, end) ranges no taller than maxHeight, preferring the
   * recorded cut points. A block taller than maxHeight is split hard at maxHeight.
   */
  segments(maxHeight: number): Array<{ start: number; end: number }> {
    const total = this.y;
    const result: Array<{ start: number; end: number }> = [];
    let start = 0;

    while (start < total) {
      const limit = start + maxHeight;

      if (total <= limit) {
        result.push({ start, end: total });
        break;
      }

      const candidates = this.cuts.filter((c) => c > start && c <= limit);
      const end = candidates.length ? candidates[candidates.length - 1] : limit;

      result.push({ start, end });
      start = end;
    }

    return result;
  }

  /** Replays all drawing onto ctx, shifted so `offsetY` lands at the top. */
  paint(ctx: CanvasRenderingContext2D, offsetY = 0) {
    ctx.save();
    ctx.translate(0, -offsetY);
    ctx.fillStyle = '#000';
    this.ops.forEach((op) => op(ctx));
    ctx.restore();
  }
}
